import { Button, Card, FormField, Heading, RadioChipGroup } from '@expressable/syntax-ds';
import { format } from 'date-fns';
import { captureException } from '@sentry/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { FieldError } from 'react-hook-form';
import {
  ContentfulTherapistSummary,
  TherapistSummaryProps,
} from 'components/ContentfulTherapistCard/ContentfulTherapistSummary';
import { EvaluationTimeSlot } from 'domains/calendars/v3/useEvaluationCalendarSummary';

const DAY_PICKER_ID = 'dayPickerInput';

type TherapistAvailability = {
  therapist: { therapistID: string };
  therapistName: string;
  timeSlots: EvaluationTimeSlot[];
};

type Props = {
  therapistAvailability: TherapistAvailability;
  index: number;
  therapistBadgesData: Omit<TherapistSummaryProps, 'therapistName'>;
  currentDay: Date;
  formFieldName: string;
  formatInTimezone: (date: Date, format: string) => string;
  onChangeTimeSlot: (slot?: EvaluationTimeSlot) => void;
  isSubmitting: boolean;
  selectedSlotId?: string;
  error?: FieldError;
};

export const TherapistAvailabilityCard: FC<Props> = ({
  therapistAvailability,
  index,
  therapistBadgesData,
  currentDay,
  formFieldName,
  formatInTimezone,
  onChangeTimeSlot,
  isSubmitting,
  selectedSlotId,
  error,
}) => {
  const { t } = useTranslation();

  return (
    <Card key={therapistAvailability.therapist.therapistID}>
      <ContentfulTherapistSummary
        {...therapistBadgesData}
        therapistName={therapistAvailability.therapistName}
        shouldShowProfile={true}
      >
        <div>
          <Heading
            level="h6"
            noDefaultMargins
            className="flex gap-e8 items-center"
            onClick={() => document.getElementById(DAY_PICKER_ID)?.focus()}
          >
            {t('directSignup.schedule.therapistAppointmentsOn', {
              date: format(currentDay, 'EEE, MMM d'),
            })}{' '}
            {index > 1 && (
              <Button variant="link" size="xs" onClick={() => document.getElementById(DAY_PICKER_ID)?.focus()}>
                {t('directSignup.schedule.changeDate')}
              </Button>
            )}
          </Heading>

          <FormField
            name={formFieldName}
            config={{
              order: { error: 'bottom', hint: 'top' },
              labelSpacing: 'lg',
            }}
          >
            <RadioChipGroup
              htmlName={`timeslot-${therapistAvailability.therapist.therapistID}`}
              chips={therapistAvailability.timeSlots.map(slot => ({
                value: slot.id,
                label: formatInTimezone(slot.time, 'h:mm a'),
              }))}
              variant="standard"
              onChange={e => {
                const pickedId = e.target.value;
                const slot = therapistAvailability.timeSlots.find(s => s.id === pickedId);
                if (!slot) {
                  captureException(new Error(`Unable to find slot with id: ${pickedId}`), {
                    extra: {
                      pickedId,
                      therapistAvailability,
                    },
                  });
                }

                onChangeTimeSlot(slot);
              }}
              disabled={isSubmitting}
              value={selectedSlotId ?? ''}
              standalone
              error={error}
              className="[&>*]:mobile-xs-d:grid-cols-2"
            />
          </FormField>
        </div>
      </ContentfulTherapistSummary>
    </Card>
  );
};
